import FormCard from "./FormCard.jsx";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { customDropdownStyles } from "../../data/data.js";
import { useEffect, useState } from "react";
import Select from "react-select";
import { useApiServiceTraining } from "../../services/apiServiceTraining.js";
import { formValidationSchemaTraining } from "../../schema/formValidationSchema.js";
import toast from "react-hot-toast";
import { formatDate } from "../../utils/utils.js";
import TimeSeriesChart from "../../charts/timeseriesChart.jsx";
import ActiveModelsTable from "../Common/ActiveModelsTable.jsx";
import ModelsDataTable from "../Common/ModelsDataTable.jsx";
import Loading from "../Common/Loading.jsx";
import ConfirmationPopup from "../Common/ConfirmationPopup.jsx";

const TrainingForm = () => {
  const {
    getProductline,
    getProductfamily,
    getAssemblies,
    getSubsystem,
    getDryGasSealTags,
    getTimeSeriesChartData,
    getDGSInfo,
    getOprSpeedInfo,
    trainModel,
    getMostImpactTags,
    getActiveModels,
  } = useApiServiceTraining();

  const [isBusinessRule, setIsBusinessRule] = useState(false);

  const [dropdownOptions, setDropdownOptions] = useState({
    productLineOptions: [],
    productFamilyOptions: [],
    assemblyOptions: [],
    dgsMakeOptions: [
      { label: "Siemens Energy", value: "Siemens Energy" },
      { label: "EagleBurgmann", value: "EagleBurgmann" },
      { label: "John Crane", value: "John Crane" },
      { label: "Flowserve", value: "Flowserve" },
      { label: "Other Manufacturer", value: "Other Manufacturer" },
    ],
    dgsTypeOptions: [
      { label: "Single gas seal", value: "Single gas seal" },
      { label: "Double opposed gas seal", value: "Double opposed gas seal" },
      {
        label: "Tandem with intermediate labyrinth",
        value: "Tandem with intermediate labyrinth",
      },
      { label: "Tandem gas seal", value: "Tandem gas seal" },
      {
        label: "Tripple seal Currently extinct",
        value: "Tripple seal Currently extinct",
      },
    ],
    dgsDirectionOptions: [
      { label: "Bidirectional", value: "Bidirectional" },
      { label: "Unidirectional", value: "Unidirectional" },
    ],
    subsystemOptions: [],
    dryGasSealTagsOptions: [],
    operatingSpeedOptions: [],
    tripSpeedOptions: [],
    designPressureOptions: [],
  });

  const [selectedValues, setSelectedValues] = useState({
    selectedProductLine: null,
    selectedProductFamily: null,
    selectedAssembly: null,
    selectedSubsystem: [],
    selectedDgsMake: null,
    selectedDgsType: null,
    selectedDgsDirection: null,
    selectedDryGasSealTags: [],
    selectedOperatingSpeed: null,
    selectedTripSpeed: null,
    selectedDesignPressure: null,
    selectedRangeMin: "",
    selectedRangeNormal: "",
    selectedRangeMax: "",
  });

  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [frequency, setFrequency] = useState(30);
  const [chartData, setChartData] = useState(null);
  const [selectedAlias, setSelectedAlias] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [businessRuleValue, setBusinessRuleValue] = useState(0);
  const [businessRuleName, setBusinessRuleName] = useState("");
  const [businessRules, setBusinessRules] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editAlias, setEditAlias] = useState(null);
  const [editOperator, setEditOperator] = useState(null);
  const [editValue, setEditValue] = useState(0);
  const [editName, setEditName] = useState("");
  const [mostImpactedTags, setMostImpactedTags] = useState([]);
  const [activeModels, setActiveModels] = useState([]);
  const [errors, setErrors] = useState({});
  const [nameDesc, setNameDesc] = useState({
    name: "",
    desc: "",
  });

  const toggleBusinessRule = () => {
    setIsBusinessRule((prev) => !prev);
  };

  // Mapping functions for each dropdown
  const mapProductLine = (item) => ({
    label: item.ASSIGNEDPL,
    value: item.ASSIGNEDPL,
    assignedPL: item.ASSIGNEDPL,
  });
  const mapProductFamily = (item) => ({
    label: item.DRIVENUNITTYPES,
    value: item.DRIVENUNITTYPES,
    drivenUnitTypes: item.DRIVENUNITTYPES,
  });
  const mapAssembly = (item) => ({
    label: item.DRIVER + " " + item.PROJECTNAME,
    value: item.DRIVER + " " + item.PROJECTNAME,
    datasource: item.DATASOURCE,
  });
  const mapSubsystem = (item) => ({
    label: item.SUBSYSTEM_NAME,
    value: item.SUBSYSTEM_NAME,
  });
  const mapDryGasSealTags = (item) => ({
    label: item.TAG_NAME,
    value: item.TAG_NAME,
    datasource: item.DATASOURCE,
    tagAlias: item.INDEP_TAG_ALIAS,
  });
  const mapOperatingSpeed = (item) => ({
    label: item.Maximum_operating_speed,
    value: item.Maximum_operating_speed,
  });
  const mapTripSpeed = (item) => ({
    label: item.Trip_speed,
    value: item.Trip_speed,
  });
  const mapDesignPressure = (item) => ({
    label: item.Design_Pressure_DGS,
    value: item.Design_Pressure_DGS,
  });

  const validateField = (fieldName, value) => {
    formValidationSchemaTraining
      .validateAt(fieldName, { [fieldName]: value })
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: null }));
      })
      .catch((validationError) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: validationError.message,
        }));
      });
  };

  // Calculate default dates
  const today = new Date();
  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() - 1); // 1 day before today

  const startDate = new Date(today);
  startDate.setMonth(startDate.getMonth() - 6); // 6 months before today

  // Add state for start and end date
  const [startDateState, setStartDateState] = useState(formatDate(startDate));
  const [endDateState, setEndDateState] = useState(formatDate(endDate));

  // Function to fetch data for a specific dropdown
  const fetchDropdownData = async (
    dropdownName,
    apiCall,
    mapFunction,
    datasource = null,
    assignedPL,
    drivenUnitTypes,
  ) => {
    try {
      const data =
        assignedPL || drivenUnitTypes
          ? await apiCall(assignedPL, drivenUnitTypes)
          : datasource
            ? await apiCall(datasource)
            : await apiCall();
      if (Array.isArray(data)) {
        const options = data.map(mapFunction);

        const defaultSubsystemValues = [
          "Seal and Separation Gas",
          "Bearings",
          "Process",
          "Suction",
          "Discharge",
          "Lub Oil System",
        ];

        setDropdownOptions((prev) => ({
          ...prev,
          [`${dropdownName}Options`]: options,
        }));

        // Set default selected values for subsystems
        if (dropdownName === "subsystem") {
          const defaultSelected = options.filter((option) =>
            defaultSubsystemValues.includes(option.value),
          );
          setSelectedValues((prev) => ({
            ...prev,
            selectedSubsystem: defaultSelected,
          }));
        }

        setDropdownOptions((prev) => ({
          ...prev,
          [`${dropdownName}Options`]: options,
        }));

        return data;
      } else {
        setDropdownOptions((prev) => ({
          ...prev,
          [`${dropdownName}Options`]: [],
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${dropdownName} data:`, error);
    }
  };

  const handleSelectChange = async (name, selectedOption) => {
    // If assembly is changed, clear DGS tags and tags as well
    if (name === "selectedAssembly") {
      setSelectedValues((prev) => ({
        ...prev,
        selectedAssembly: selectedOption,
        selectedSubsystem: null,
        selectedDryGasSealTags: null,
        selectedRangeMin: "",
        selectedRangeNormal: "",
        selectedRangeMax: "",
      }));
    }
    if (name === "selectedSubsystem") {
      setSelectedValues((prev) => ({
        ...prev,
        selectedSubsystem: selectedOption,
      }));
    } else {
      setSelectedValues((prev) => ({ ...prev, [name]: selectedOption }));
    }

    const fieldsToValidate = ["selectedAssembly", "selectedDryGasSealTags"];

    if (fieldsToValidate.includes(name)) {
      validateField(name, selectedOption);
    }

    // Determine the current values for assignedPL and drivenUnitTypes
    const assignedPL =
      name === "selectedProductLine"
        ? selectedOption?.value
        : selectedValues.selectedProductLine?.value;
    const drivenUnitTypes =
      name === "selectedProductFamily"
        ? selectedOption?.value
        : selectedValues.selectedProductFamily?.value;
    const dataSourceForDgsTags =
      name === "selectedAssembly"
        ? selectedOption?.datasource
        : selectedValues.selectedAssembly?.datasource;

    // Recall assembly API when product line or family changes or is cleared
    if (name === "selectedProductLine" || name === "selectedProductFamily") {
      setLoading(true);

      try {
        // If cleared, assignedPL or drivenUnitTypes will be undefined/null
        await fetchDropdownData(
          "assembly",
          () => getAssemblies(assignedPL, drivenUnitTypes),
          mapAssembly,
        );
      } catch (e) {
        console.log(e);
        toast.error("Error fetching assemblies. Please try again.");
      } finally {
        setLoading(false);
      }
    } else if (name === "selectedAssembly" && selectedOption) {
      const datasource = selectedOption.datasource;

      // Set loading state to true
      setLoading(true);

      try {
        await fetchDropdownData(
          "subsystem",
          getSubsystem,
          mapSubsystem,
          datasource,
        );
        await fetchDropdownData(
          "dryGasSealTags",
          getDryGasSealTags,
          mapDryGasSealTags,
          datasource,
        );

        const dgsInfo = await getDGSInfo(datasource);
        const oprSpeedInfo = await getOprSpeedInfo(datasource);

        if (dgsInfo) {
          const opSpeedOptions = dgsInfo.map(mapOperatingSpeed);
          setDropdownOptions((prev) => ({
            ...prev,
            [`operatingSpeedOptions`]: opSpeedOptions,
          }));

          const tripSpeedoptions = dgsInfo.map(mapTripSpeed);
          setDropdownOptions((prev) => ({
            ...prev,
            [`tripSpeedOptions`]: tripSpeedoptions,
          }));

          const designPressureOptions = dgsInfo.map(mapDesignPressure);
          setDropdownOptions((prev) => ({
            ...prev,
            [`designPressureOptions`]: designPressureOptions,
          }));

          if (opSpeedOptions.length === 1) {
            setSelectedValues((prev) => ({
              ...prev,
              selectedOperatingSpeed: opSpeedOptions[0],
            }));
          }
          if (tripSpeedoptions.length === 1) {
            setSelectedValues((prev) => ({
              ...prev,
              selectedTripSpeed: tripSpeedoptions[0],
            }));
          }
          if (designPressureOptions.length === 1) {
            setSelectedValues((prev) => ({
              ...prev,
              selectedDesignPressure: designPressureOptions[0],
            }));
          }
        }

        if (Array.isArray(oprSpeedInfo) && oprSpeedInfo.length > 0) {
          const { rangeMin, rangeNormal, rangeMax } = oprSpeedInfo[0] || {};
          setSelectedValues((prev) => ({
            ...prev,
            selectedRangeMin:
              rangeMin !== null && rangeMin !== undefined ? String(rangeMin) : "",
            selectedRangeNormal:
              rangeNormal !== null && rangeNormal !== undefined
                ? String(rangeNormal)
                : "",
            selectedRangeMax:
              rangeMax !== null && rangeMax !== undefined ? String(rangeMax) : "",
          }));
        }
      } catch (e) {
        toast.error(
          "Error fetching subsystem/ dry gas seal tags/ dgsInfo/ oprSpeedInfo. Please try again.",
        );
        console.log(e);
      } finally {
        // Set loading state to false after fetching data
        setLoading(false);
      }
    } else if (name === "selectedSubsystem" && selectedOption) {
      const subsystemNames = selectedOption.map((option) => option.value);

      // Set loading state to true
      setLoading(true);

      try {
        await fetchDropdownData(
          "dryGasSealTags",
          () => getDryGasSealTags(dataSourceForDgsTags, subsystemNames),
          mapDryGasSealTags,
        );
      } catch (e) {
        console.log(e);
        toast.error("Error fetching dry gas seal tags. Please try again.");
      } finally {
        // Set loading state to false after fetching data
        setLoading(false);
      }
    } else if (name === "selectedDryGasSealTags" && selectedOption) {
      const tagAliases = selectedOption.map((option) => option.tagAlias);

      setMostImpactedTags([]);
      setActiveModels([]);

      setLoading(true);
      try {
        const activeModels = await getActiveModels(
          tagAliases,
          dataSourceForDgsTags,
        );
        setActiveModels(activeModels);
      } catch (e) {
        toast.error("Error fetching active models. Please try again.");
        console.log("Error fetching active models:", e);
      } finally {
        setLoading(false);
      }

      if (selectedOption.length >= 3) {
        const business_rules = businessRules.map((rule) => ({
          ALIAS: rule.alias?.value || rule.alias,
          OPERATOR: rule.operator?.value || rule.operator,
          VALUE: rule.value,
        }));

        try {
          const impactedTags = await getMostImpactTags(
            tagAliases,
            dataSourceForDgsTags,
            frequency,
            startDateState,
            endDateState,
            business_rules,
          );
          setMostImpactedTags(impactedTags);
        } catch (e) {
          toast.error("Error fetching impacted tags. Please try again.");
          console.log("Error fetching impacted tags:", e);
        }
      }
    }
  };

  // Fetch chart data when frequency, startDateState, endDateState, or selectedDryGasSealTags changes
  useEffect(() => {
    const fetchChartData = async () => {
      if (
        selectedValues.selectedDryGasSealTags &&
        selectedValues.selectedDryGasSealTags.length > 0 &&
        selectedValues.selectedAssembly
      ) {
        const tagAliases = selectedValues.selectedDryGasSealTags.map(
          (option) => option.tagAlias,
        );
        const datasources = selectedValues.selectedDryGasSealTags.map(
          (option) => option.datasource,
        );

        // Prepare business_rules array for API
        const business_rules = businessRules.map((rule) => ({
          ALIAS: rule.alias?.value || rule.alias,
          OPERATOR: rule.operator?.value || rule.operator,
          VALUE: rule.value,
        }));

        setChartLoading(true);
        setLoading(true);
        try {
          const data = await getTimeSeriesChartData(
            startDateState,
            endDateState,
            datasources[0],
            tagAliases,
            frequency,
            business_rules,
          );

          setChartData(data);
        } catch (error) {
          setLoading(false);
          setChartLoading(false);
          toast.error(
            "Error fetching time series chart data. Please try again.",
          );
          console.error("Error fetching time series chart data:", error);
        } finally {
          setLoading(false);
          setChartLoading(false);
        }
      }
    };

    fetchChartData();
  }, [
    frequency,
    startDateState,
    endDateState,
    selectedValues.selectedDryGasSealTags,
    selectedValues.selectedAssembly,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchDropdownData("productLine", getProductline, mapProductLine);
      await fetchDropdownData(
        "productFamily",
        getProductfamily,
        mapProductFamily,
      );
      await fetchDropdownData("assembly", getAssemblies, mapAssembly);

      setLoading(false);
    };

    fetchData();
  }, []);

  const isBusinessRuleAddEnabled =
    selectedAlias &&
    selectedOperator &&
    businessRuleValue !== "" &&
    businessRuleName.trim() !== "";

  const handleAddBusinessRule = () => {
    const newRule = {
      alias: selectedAlias,
      operator: selectedOperator,
      value: Number(businessRuleValue),
      name: businessRuleName,
    };
    if (editIndex !== null) {
      // Edit mode
      setBusinessRules((rules) =>
        rules.map((r, i) => (i === editIndex ? newRule : r)),
      );
      setEditIndex(null);
    } else {
      // Add mode
      setBusinessRules((rules) => [...rules, newRule]);
    }
    // Clear the input fields after add/update
    setSelectedAlias(null);
    setSelectedOperator(null);
    setBusinessRuleValue(0);
    setBusinessRuleName("");
  };

  const handleDeleteBusinessRule = (idx) => {
    setBusinessRules((rules) => rules.filter((_, i) => i !== idx));
  };

  const handleEditBusinessRule = (idx) => {
    const rule = businessRules[idx];
    setEditAlias(rule.alias);
    setEditOperator(rule.operator);
    setEditValue(rule.value);
    setEditName(rule.name);
    setEditIndex(idx);
  };

  // Add this function inside TrainingForm
  const handleClearAllBusinessRules = async () => {
    setBusinessRules([]);
    setSelectedAlias(null);
    setSelectedOperator(null);
    setBusinessRuleValue(0);
    setBusinessRuleName("");
    setEditIndex(null);

    // Call chart API again with no business rules
    if (
      selectedValues.selectedDryGasSealTags &&
      selectedValues.selectedDryGasSealTags.length > 0 &&
      selectedValues.selectedAssembly
    ) {
      const tagAliases = selectedValues.selectedDryGasSealTags.map(
        (option) => option.tagAlias,
      );
      const datasources = selectedValues.selectedDryGasSealTags.map(
        (option) => option.datasource,
      );

      setChartLoading(true);
      setLoading(true);
      try {
        const data = await getTimeSeriesChartData(
          startDateState,
          endDateState,
          datasources[0],
          tagAliases,
          frequency,
          [], // No business rules
        );
        setChartData(data);
      } catch (error) {
        setLoading(false);
        setChartLoading(false);
        console.error("Error while clearing business rules:", error);
      } finally {
        setLoading(false);
        setChartLoading(false);
      }
    }
  };

  // Add this function inside TrainingForm
  const handleSaveEditBusinessRule = () => {
    const updatedRule = {
      alias: editAlias,
      operator: editOperator,
      value: Number(editValue),
      name: editName,
    };
    setBusinessRules((rules) =>
      rules.map((r, i) => (i === editIndex ? updatedRule : r)),
    );
    setEditIndex(null);
    setEditAlias(null);
    setEditOperator(null);
    setEditValue("");
    setEditName("");
  };

  const handleApplyBusinessRules = async () => {
    if (
      selectedValues.selectedDryGasSealTags &&
      selectedValues.selectedDryGasSealTags.length > 0 &&
      selectedValues.selectedAssembly
    ) {
      const tagAliases = selectedValues.selectedDryGasSealTags.map(
        (option) => option.tagAlias,
      );
      const datasources = selectedValues.selectedDryGasSealTags.map(
        (option) => option.datasource,
      );

      const business_rules = businessRules.map((rule) => ({
        ALIAS: rule.alias?.value || rule.alias,
        OPERATOR: rule.operator?.value || rule.operator,
        VALUE: rule.value,
      }));

      setChartLoading(true);
      setLoading(true);
      try {
        const data = await getTimeSeriesChartData(
          startDateState,
          endDateState,
          datasources[0],
          tagAliases,
          frequency,
          business_rules,
        );
        setChartData(data);
      } catch (error) {
        toast.error("Error applying business rules. Please try again.");
        setLoading(false);
        setChartLoading(false);
        console.error("Error applying business rules:", error);
      } finally {
        setLoading(false);
        setChartLoading(false);
      }
    }
  };

  const clearFormData = () => {
    // Clear form data after training starts
    setSelectedValues({
      selectedProductLine: null,
      selectedProductFamily: null,
      selectedAssembly: null,
      selectedSubsystem: [],
      selectedDgsMake: null,
      selectedDgsType: null,
      selectedDgsDirection: null,
      selectedDryGasSealTags: [],
      selectedOperatingSpeed: null,
      selectedTripSpeed: null,
      selectedDesignPressure: null,
      selectedRangeMin: "",
      selectedRangeNormal: "",
      selectedRangeMax: "",
    });
    setBusinessRules([]);
    setChartData(null);
    setMostImpactedTags([]);
    setActiveModels([]);
    setNameDesc({ name: "", desc: "" });
    setErrors({});
  };

  const handleTrainModel = async () => {
    const tagAliases = selectedValues.selectedDryGasSealTags.map(
      (option) => option.tagAlias,
    );

    const tagName = selectedValues.selectedDryGasSealTags.map((option) =>
      option.value.replace(/\s*\(.*?\)\s*/g, "").trim(),
    );
    const datasources = selectedValues.selectedDryGasSealTags.map(
      (option) => option.datasource,
    );

    const business_rules = businessRules.map((rule) => ({
      ALIAS: rule.alias?.value || rule.alias,
      OPERATOR: rule.operator?.value || rule.operator,
      VALUE: rule.value,
    }));

    const dgsInfo = [
      {
        "DGS Make": selectedValues.selectedDgsMake?.value || "",
        "DGS Type": selectedValues.selectedDgsType?.value || "",
        "DGS Direction": selectedValues.selectedDgsDirection?.value || "",
        "M/C design pressure":
          typeof selectedValues.selectedDesignPressure === "object"
            ? selectedValues.selectedDesignPressure?.value || ""
            : selectedValues.selectedDesignPressure || "",
        "M/C max operating speed":
          typeof selectedValues.selectedOperatingSpeed === "object"
            ? selectedValues.selectedOperatingSpeed?.value || ""
            : selectedValues.selectedOperatingSpeed || "",
        "M/C trip speed":
          typeof selectedValues.selectedTripSpeed === "object"
            ? selectedValues.selectedTripSpeed?.value || ""
            : selectedValues.selectedTripSpeed || "",
        "Range Minimum": selectedValues.selectedRangeMin || "",
        "Range Normal": selectedValues.selectedRangeNormal || "",
        "Range Maximum": selectedValues.selectedRangeMax || "",
      },
    ];

    setLoading(true);
    try {
      await trainModel({
        tagName,
        tagAlias: tagAliases,
        datasource_id: datasources[0],
        frequency,
        startDate: startDateState,
        endDate: endDateState,
        businessRules: business_rules ?? [],
        dgsInfo: dgsInfo ?? [],
        name: nameDesc.name,
        desc: nameDesc.desc,
      });

      const params = new URLSearchParams({
        datasource: datasources[0],
      });

      tagAliases.forEach((tag) => {
        params.append("tags", tag);
      });

      params.append("tagName", tagName.join(","));

      const url = `${window.location.origin}/models?${params.toString()}`;

      setTimeout(() => {
        window.open(url, "_blank");
        setLoading(false);
      }, 10000);

      clearFormData();
    } catch (error) {
      console.error("Error while handling trainModel:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      chartData &&
      Array.isArray(chartData.values) &&
      chartData.values.length > 0
    ) {
      // Check if all inner array's last elements are null
      const allLastElementsNull = chartData.values.every(
        (arr) =>
          Array.isArray(arr) && arr.length > 0 && arr[arr.length - 1] === null,
      );
      if (allLastElementsNull) {
        setIsPopupVisible(true);
      }
    }
  }, [chartData]);

  return (
    <>
      {loading && <Loading />}
      <div className="flex justify-center flex-col gap-10 items-center mt-4 mb-40">
        <FormCard>
          <div>
            <span className="font-semibold text-2xl text-btn-primary">
              Training
            </span>
            <p className="mt-3 text-[14.5px]">
              Train a model using the barrier seal and dry gas seal tags, along
              with their respective assemblies, by selecting a time period that
              exhibits normal operational behavior in their values.
            </p>

            <form action="" className="mt-8">
              {/* product line, family, assembly */}
              <div className="flex  gap-4">
                <div className="flex flex-col w-full">
                  <label htmlFor="productLine" className="mb-1">
                    Product Line{" "}
                  </label>
                  <Select
                    id="productLine"
                    className="w-full"
                    placeholder="Select Product Line"
                    options={dropdownOptions.productLineOptions.filter(
                      (opt) =>
                        opt.value !== null &&
                        opt.value !== undefined &&
                        opt.value !== "",
                    )}
                    isClearable
                    isSearchable
                    styles={customDropdownStyles}
                    value={selectedValues.selectedProductLine}
                    onChange={(option) =>
                      handleSelectChange("selectedProductLine", option)
                    }
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label htmlFor="productFamily" className="mb-1">
                    Product Family{" "}
                  </label>
                  <Select
                    id="productFamily"
                    width="100%"
                    placeholder="Select Product Family"
                    styles={customDropdownStyles}
                    options={dropdownOptions.productFamilyOptions.filter(
                      (opt) =>
                        opt.value !== null &&
                        opt.value !== undefined &&
                        opt.value !== "",
                    )}
                    isClearable
                    isSearchable
                    value={selectedValues.selectedProductFamily}
                    onChange={(option) =>
                      handleSelectChange("selectedProductFamily", option)
                    }
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label htmlFor="assembly" className="mb-1">
                    Assembly*
                  </label>
                  <Select
                    id="assembly"
                    className="w-full"
                    placeholder="Select Assembly"
                    options={dropdownOptions.assemblyOptions.filter(
                      (opt) =>
                        opt.value !== null &&
                        opt.value !== undefined &&
                        opt.value !== "",
                    )}
                    isClearable
                    isSearchable
                    required
                    styles={customDropdownStyles}
                    value={selectedValues.selectedAssembly}
                    onChange={(option) =>
                      handleSelectChange("selectedAssembly", option)
                    }
                  />
                  {errors.selectedAssembly && (
                    <p className="text-red-500 text-[14.5px] mt-1">
                      {errors.selectedAssembly}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex  gap-4 mt-2 ">
                <div className="flex flex-col w-full">

                   <div className="flex flex-col w-full mb-1.5">
                    <label htmlFor="subsystem" className="mb-1">
                      Subsytem
                    </label>
                    <Select
                      id="subsystem"
                      placeholder="Select Subsystem"
                      options={dropdownOptions.subsystemOptions.filter(
                        (opt) =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== "",
                      )}
                      isClearable
                      isSearchable
                      styles={customDropdownStyles}
                      value={selectedValues.selectedSubsystem}
                      isMulti
                      onChange={(option) =>
                        handleSelectChange("selectedSubsystem", option)
                      }
                    />
                  </div>

                  <label htmlFor="dgs-tags" className="mb-1">
                    Dry Gas Seal Tags*
                  </label>
                  <Select
                    id="dgs-tags"
                    placeholder="Select Dry Gas Seal Tags"
                    isClearable
                    isMulti
                    isSearchable
                    required
                    styles={customDropdownStyles}
                    options={dropdownOptions.dryGasSealTagsOptions.filter(
                      (opt) =>
                        opt.value !== null &&
                        opt.value !== undefined &&
                        opt.value !== "",
                    )}
                    value={selectedValues.selectedDryGasSealTags || []}
                    onChange={(option) =>
                      handleSelectChange("selectedDryGasSealTags", option)
                    }
                    isOptionDisabled={() =>
                      Array.isArray(selectedValues.selectedDryGasSealTags) &&
                      selectedValues.selectedDryGasSealTags.length > 5
                    }
                    isDisabled={chartLoading}
                  />
                  {selectedValues.selectedDryGasSealTags &&
                  selectedValues.selectedDryGasSealTags.length >= 3 &&
                  mostImpactedTags.length !== 0 ? (
                    <div className="flex gap-1">
                      <p className="text-green-500 text-[14px] cursor-pointer mt-1 font-semibold">
                        Suggested most impacted tags:{" "}
                        {mostImpactedTags.join(", ")}{" "}
                      </p>{" "}
                    </div>
                  ) : (
                    ""
                  )}

                  {errors.selectedDryGasSealTags && (
                    <p className="text-red-500 text-[14.5px] mt-1">
                      {errors.selectedDryGasSealTags}
                    </p>
                  )}
                </div>
              </div>

              {/* DGS , M/C*/}
              <div className="flex flex-col">
                {/* DGS make, DGS type,DGS direction)  */}

                <div className="flex gap-4">
                 
                  <div className="flex flex-col mt-2 w-full">
                    <label htmlFor="dgs-make" className="mb-1">
                      DGS Make
                    </label>
                    <Select
                      id="dgs-make"
                      placeholder="Select DGS Make"
                      options={dropdownOptions.dgsMakeOptions.filter(
                        (opt) =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== "",
                      )}
                      isClearable
                      isSearchable
                      styles={customDropdownStyles}
                      value={
                        selectedValues.selectedDgsMake
                          ? selectedValues.selectedDgsMake
                          : dropdownOptions.dgsMakeOptions.length === 1
                            ? dropdownOptions.dgsMakeOptions[0]
                            : ""
                      }
                      onChange={(option) =>
                        handleSelectChange("selectedDgsMake", option)
                      }
                    />
                  </div>

                  <div className="flex flex-col mt-2 w-full">
                    <label htmlFor="dgs-type" className="mb-1">
                      DGS Type
                    </label>
                    <Select
                      id="dgs-type"
                      placeholder="Select DGS Type"
                      options={dropdownOptions.dgsTypeOptions.filter(
                        (opt) =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== "",
                      )}
                      isClearable
                      isSearchable
                      styles={customDropdownStyles}
                      value={
                        selectedValues.selectedDgsType
                          ? selectedValues.selectedDgsType
                          : dropdownOptions.dgsTypeOptions.length === 1
                            ? dropdownOptions.dgsTypeOptions[0]
                            : null
                      }
                      onChange={(option) =>
                        handleSelectChange("selectedDgsType", option)
                      }
                    />
                  </div>

                  <div className="flex flex-col mt-2 w-full">
                    <label htmlFor="dgs-direction" className="mb-1">
                      DGS Direction
                    </label>
                    <Select
                      id="dgs-direction"
                      placeholder="Select DGS Direction"
                      options={dropdownOptions.dgsDirectionOptions.filter(
                        (opt) =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== "",
                      )}
                      isClearable
                      isSearchable
                      styles={customDropdownStyles}
                      value={
                        selectedValues.selectedDgsDirection
                          ? selectedValues.selectedDgsDirection
                          : dropdownOptions.dgsDirectionOptions.length === 1
                            ? dropdownOptions.dgsDirectionOptions[0]
                            : null
                      }
                      onChange={(option) =>
                        handleSelectChange("selectedDgsDirection", option)
                      }
                    />
                  </div>
                </div>

                {/* M/C speed, M/C trip, M/C pressure */}
                <div className="flex gap-4">
                  <div className="flex flex-col mt-2 w-full">
                    <label htmlFor="max-speed" className="mb-1">
                      M/C max operating speed
                    </label>
                    {!dropdownOptions.operatingSpeedOptions[0]?.value ? (
                      <input
                        type="text"
                        id="max-speed"
                        placeholder="Enter M/C max speed"
                        value={
                          typeof selectedValues.selectedOperatingSpeed ===
                            "object" &&
                          selectedValues.selectedOperatingSpeed !== null
                            ? selectedValues.selectedOperatingSpeed.value || ""
                            : selectedValues.selectedOperatingSpeed || ""
                        }
                        onChange={(e) =>
                          handleSelectChange(
                            "selectedOperatingSpeed",
                            e.target.value,
                          )
                        }
                        className="custom-input w-full"
                      />
                    ) : (
                      <Select
                        id="max-speed"
                        placeholder="Select M/C max speed"
                        options={dropdownOptions.operatingSpeedOptions.filter(
                          (opt) =>
                            opt.value !== null &&
                            opt.value !== undefined &&
                            opt.value !== "",
                        )}
                        isClearable
                        isSearchable
                        styles={customDropdownStyles}
                        value={
                          selectedValues.selectedOperatingSpeed
                            ? selectedValues.selectedOperatingSpeed
                            : dropdownOptions.operatingSpeedOptions.length === 1
                              ? dropdownOptions.operatingSpeedOptions[0]
                              : null
                        }
                        onChange={(option) =>
                          handleSelectChange("selectedOperatingSpeed", option)
                        }
                      />
                    )}
                  </div>

                  <div className="flex flex-col mt-2 w-full">
                    <label htmlFor="trip-speed" className="mb-1">
                      M/C trip speed
                    </label>
                    {!dropdownOptions.tripSpeedOptions[0]?.value ? (
                      <input
                        type="text"
                        id="trip-speed"
                        placeholder="Enter M/C trip speed"
                        value={
                          typeof selectedValues.selectedTripSpeed ===
                            "object" &&
                          selectedValues.selectedTripSpeed !== null
                            ? selectedValues.selectedTripSpeed.value || ""
                            : selectedValues.selectedTripSpeed || ""
                        }
                        onChange={(e) =>
                          handleSelectChange(
                            "selectedTripSpeed",
                            e.target.value,
                          )
                        }
                        className="custom-input w-full"
                      />
                    ) : (
                      <Select
                        id="trip-speed"
                        placeholder="Select M/C speed"
                        options={dropdownOptions.tripSpeedOptions.filter(
                          (opt) =>
                            opt.value !== null &&
                            opt.value !== undefined &&
                            opt.value !== "",
                        )}
                        isClearable
                        isSearchable
                        styles={customDropdownStyles}
                        value={
                          selectedValues.selectedTripSpeed
                            ? selectedValues.selectedTripSpeed
                            : dropdownOptions.tripSpeedOptions.length === 1
                              ? dropdownOptions.tripSpeedOptions[0]
                              : null
                        }
                        onChange={(option) =>
                          handleSelectChange("selectedTripSpeed", option)
                        }
                      />
                    )}
                  </div>

                  <div className="flex flex-col mt-2 w-full">
                    <label htmlFor="pressure" className="mb-1">
                      M/C design pressure
                    </label>
                    {!dropdownOptions.designPressureOptions[0]?.value ? (
                      <input
                        type="text"
                        id="pressure"
                        placeholder="Enter M/C design pressure"
                        value={
                          typeof selectedValues.selectedDesignPressure ===
                            "object" &&
                          selectedValues.selectedDesignPressure !== null
                            ? selectedValues.selectedDesignPressure.value || ""
                            : selectedValues.selectedDesignPressure || ""
                        }
                        onChange={(e) =>
                          handleSelectChange(
                            "selectedDesignPressure",
                            e.target.value,
                          )
                        }
                        className="custom-input w-full"
                      />
                    ) : (
                      <Select
                        id="pressure"
                        placeholder="Select M/C pressure"
                        options={dropdownOptions.designPressureOptions.filter(
                          (opt) =>
                            opt.value !== null &&
                            opt.value !== undefined &&
                            opt.value !== "",
                        )}
                        isClearable
                        isSearchable
                        styles={customDropdownStyles}
                        value={
                          selectedValues.selectedDesignPressure
                            ? selectedValues.selectedDesignPressure
                            : dropdownOptions.designPressureOptions.length === 1
                              ? dropdownOptions.designPressureOptions[0]
                              : null
                        }
                        onChange={(option) =>
                          handleSelectChange("selectedDesignPressure", option)
                        }
                      />
                    )}
                  </div>
                </div>

                <div className="flex flex-col  gap-4">
                  <div className="w-full">
                    <div className="flex gap-4">
                      <div className="flex flex-col mt-2 w-full">
                        <label htmlFor="range-min" className="mb-1">
                          Range Minimum
                        </label>
                        <input
                          type="text"
                          id="range-min"
                          placeholder="Enter range minimum"
                          value={selectedValues.selectedRangeMin || ""}
                          onChange={(e) =>
                            handleSelectChange("selectedRangeMin", e.target.value)
                          }
                          className="custom-input w-full"
                        />
                      </div>

                      <div className="flex flex-col mt-2 w-full">
                        <label htmlFor="range-normal" className="mb-1">
                          Range Normal
                        </label>
                        <input
                          type="text"
                          id="range-normal"
                          placeholder="Enter range normal"
                          value={selectedValues.selectedRangeNormal || ""}
                          onChange={(e) =>
                            handleSelectChange("selectedRangeNormal", e.target.value)
                          }
                          className="custom-input w-full"
                        />
                      </div>

                      <div className="flex flex-col mt-2 w-full">
                        <label htmlFor="range-max" className="mb-1">
                          Range Maximum
                        </label>
                        <input
                          type="text"
                          id="range-max"
                          placeholder="Enter range maximum"
                          value={selectedValues.selectedRangeMax || ""}
                          onChange={(e) =>
                            handleSelectChange("selectedRangeMax", e.target.value)
                          }
                          className="custom-input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="w-full">
                    <div className="flex gap-4">
                      <div className="flex flex-col mt-2  w-full">
                        <label htmlFor="start-date" className="mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          id="start-date"
                          className="custom-input"
                          value={startDateState}
                          onChange={(e) => setStartDateState(e.target.value)}
                        />
                      </div>

                      <div className="flex flex-col mt-2  w-full">
                        <label htmlFor="end-date" className="mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          id="end-date"
                          className="custom-input "
                          value={endDateState}
                          onChange={(e) => setEndDateState(e.target.value)}
                        />
                      </div>

                      <div className="flex flex-col mt-2 w-full">
                        <label htmlFor="frequency" className="mb-1">
                          Frequency{" "}
                          {frequency !== "" ? (
                            <i> = {frequency} mins</i>
                          ) : (
                            ""
                          )}{" "}
                        </label>
                        <div className="w-full flex items-end">
                          <input
                            type="number"
                            id="frequency"
                            placeholder="Enter frequency in mins"
                            value={frequency}
                            min={1}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              if (e.target.value === "" || val < 1) {
                                setFrequency("");
                              } else {
                                setFrequency(val);
                              }
                            }}
                            className="custom-input w-full"
                          />
                          <span className="text-gray-500 ml-2">mins</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex flex-col mt-2  w-full">
                      <label htmlFor="name" className="mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        placeholder="Enter name"
                        className="custom-input"
                        value={nameDesc.name}
                        onChange={(e) =>
                          setNameDesc((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="flex flex-col mt-2  w-full">
                      <label htmlFor="description" className="mb-1">
                        Description (optional)
                      </label>
                      <input
                        type="text"
                        id="description"
                        placeholder="Enter description"
                        className="custom-input"
                        value={nameDesc.desc}
                        onChange={(e) =>
                          setNameDesc((prev) => ({
                            ...prev,
                            desc: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 ">
                <div
                  className="flex justify-between items-center  hover:bg-gray-100 py-3 px-2 hover:cursor-pointer"
                  onClick={toggleBusinessRule}
                >
                  <span className="text-btn-primary font-semibold flex items-center text-[16.5px]">
                    Business Rule{" "}
                  </span>
                  {isBusinessRule ? (
                    <IoIosArrowDown
                      size={20}
                      className="text-btn-primary cursor-pointer"
                      onClick={toggleBusinessRule}
                    />
                  ) : (
                    <IoIosArrowUp
                      size={20}
                      className="text-btn-primary cursor-pointer"
                      onClick={toggleBusinessRule}
                    />
                  )}
                </div>

                {isBusinessRule ? (
                  <>
                    <div className="flex gap-4 mt-3 items-center justify-center px-3">
                      <div className="flex flex-col w-full">
                        <label htmlFor="rule-name" className="mb-1">
                          Alias{" "}
                        </label>
                        <Select
                          id="rule-name"
                          placeholder="Select Alias"
                          options={(
                            selectedValues.selectedDryGasSealTags || []
                          ).map((tag) => ({
                            label: `${tag.tagAlias}`,
                            value: tag.tagAlias,
                          }))}
                          value={selectedAlias}
                          isClearable
                          isSearchable
                          styles={customDropdownStyles}
                          onChange={setSelectedAlias}
                        />
                      </div>

                      <div className="flex flex-col w-full">
                        <label className="mb-1" htmlFor="operator">
                          Operator
                        </label>
                        <Select
                          id="operator"
                          placeholder="Select Operator"
                          options={[
                            { label: "greater", value: "greater" },
                            { label: "lower", value: "lower" },
                            { label: "equal", value: "equal" },
                            { label: "not equal", value: "not equal" },
                          ]}
                          isClearable
                          styles={customDropdownStyles}
                          value={selectedOperator}
                          onChange={setSelectedOperator}
                        />
                      </div>
                      <div className="flex flex-col w-full">
                        <label htmlFor="value" className="mb-1">
                          Value
                        </label>
                        <input
                          type="number"
                          id="value"
                          min={0}
                          value={businessRuleValue}
                          onChange={(e) => setBusinessRuleValue(e.target.value)}
                          className="custom-input "
                        />
                      </div>
                      <div className="flex flex-col w-full">
                        <label htmlFor="business_rule_name" className="mb-1">
                          Business Rule Name
                        </label>
                        <input
                          type="text"
                          id="business_rule_name"
                          placeholder="CUST_BR_NAME"
                          value={businessRuleName}
                          onChange={(e) => setBusinessRuleName(e.target.value)}
                          className="custom-input "
                        />
                      </div>
                      <div className="flex w-full gap-2 justify-center mt-6">
                        <button
                          type="button"
                          className={`button-primary ${
                            !isBusinessRuleAddEnabled
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          disabled={!isBusinessRuleAddEnabled}
                          onClick={handleAddBusinessRule}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className={`button-red`}
                          onClick={handleClearAllBusinessRules}
                        >
                          Clear all
                        </button>
                      </div>
                    </div>

                    {businessRules.length > 0 && (
                      <div className="mt-7">
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-300 rounded">
                            <thead>
                              <tr className="bg-gray-100 font-normal">
                                <th className="px-3 py-2 border-b text-left">
                                  Alias
                                </th>
                                <th className="px-3 py-2 border-b text-left">
                                  Operator
                                </th>
                                <th className="px-3 py-2 border-b text-left">
                                  Value
                                </th>
                                <th className="px-3 py-2 border-b text-left">
                                  Business Rule Name
                                </th>
                                <th className="px-3 py-2 border-b text-left">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {businessRules.map((rule, idx) => (
                                <tr key={idx} className="border-b">
                                  {editIndex === idx ? (
                                    <>
                                      <td className="px-3 py-2 w-[210px]">
                                        <Select
                                          id={`edit-alias-${idx}`}
                                          maxMenuHeight={80}
                                          placeholder="Select Alias"
                                          options={(
                                            selectedValues.selectedDryGasSealTags ||
                                            []
                                          ).map((tag) => ({
                                            label: `${tag.tagAlias}`,
                                            value: tag.tagAlias,
                                          }))}
                                          value={editAlias}
                                          isClearable
                                          isSearchable
                                          styles={customDropdownStyles}
                                          onChange={setEditAlias}
                                        />
                                      </td>
                                      <td className="px-3 py-2 w-[210px]">
                                        <Select
                                          id={`edit-operator-${idx}`}
                                          maxMenuHeight={80}
                                          placeholder="Select Operator"
                                          options={[
                                            {
                                              label: "greater",
                                              value: "greater",
                                            },
                                            { label: "lower", value: "lower" },
                                            { label: "equal", value: "equal" },
                                            {
                                              label: "not equal",
                                              value: "not equal",
                                            },
                                          ]}
                                          isClearable
                                          styles={customDropdownStyles}
                                          value={editOperator}
                                          onChange={setEditOperator}
                                        />
                                      </td>
                                      <td className="w-[210px]">
                                        <input
                                          type="number"
                                          min={0}
                                          value={editValue}
                                          onChange={(e) =>
                                            setEditValue(e.target.value)
                                          }
                                          className="custom-input"
                                        />
                                      </td>
                                      <td className="w-[210px]">
                                        <input
                                          type="text"
                                          placeholder="CUST_BR_NAME"
                                          value={editName}
                                          onChange={(e) =>
                                            setEditName(e.target.value)
                                          }
                                          className="custom-input"
                                        />
                                      </td>
                                      <td className="px-3 py-2 flex gap-2">
                                        <button
                                          type="button"
                                          className={`button-primary px-2 py-1 text-base ${
                                            !(
                                              editAlias &&
                                              editOperator &&
                                              editValue !== "" &&
                                              editName.trim() !== ""
                                            )
                                              ? "opacity-50 cursor-not-allowed"
                                              : ""
                                          }`}
                                          disabled={
                                            !(
                                              editAlias &&
                                              editOperator &&
                                              editValue !== "" &&
                                              editName.trim() !== ""
                                            )
                                          }
                                          onClick={handleSaveEditBusinessRule}
                                        >
                                          Save
                                        </button>
                                        <button
                                          type="button"
                                          className="button-red px-2 py-1 text-base"
                                          onClick={() =>
                                            handleDeleteBusinessRule(null)
                                          }
                                        >
                                          Delete
                                        </button>
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="px-3 py-2">
                                        {rule.alias?.label || rule.alias?.value}
                                      </td>
                                      <td className="px-3 py-2">
                                        {rule.operator?.label ||
                                          rule.operator?.value}
                                      </td>
                                      <td className="px-3 py-2">
                                        {rule.value}
                                      </td>
                                      <td className="px-3 py-2">{rule.name}</td>
                                      <td className="px-3 py-2 flex gap-2">
                                        <button
                                          type="button"
                                          className="button-primary px-2 py-1 text-base"
                                          onClick={() =>
                                            handleEditBusinessRule(idx)
                                          }
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="button-red px-2 py-1 text-base"
                                          onClick={() =>
                                            handleDeleteBusinessRule(idx)
                                          }
                                        >
                                          Delete
                                        </button>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <button
                            onClick={handleApplyBusinessRules}
                            type="button"
                            className={`bg-btn-primary px-10 text-white p-1.5 my-5 rounded-full h-10`}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  ""
                )}
              </div>
            </form>
          </div>

          <button
            type="submit"
            onClick={() =>
              toast.promise(handleTrainModel(), {
                loading: "Applying changes...",
                success: <b>Training started!</b>,
                error: <b>Failed to start the training.</b>,
              })
            }
            disabled={
              !(
                selectedValues.selectedAssembly &&
                Array.isArray(selectedValues.selectedDryGasSealTags) &&
                selectedValues.selectedDryGasSealTags.length !== 0
              )
            }
            className={`button-primary  ${
              !(
                selectedValues.selectedAssembly &&
                Array.isArray(selectedValues.selectedDryGasSealTags) &&
                selectedValues.selectedDryGasSealTags.length !== 0
              )
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            {` Train Model `}
          </button>
        </FormCard>

        {activeModels.length !== 0 &&
        selectedValues.selectedAssembly &&
        selectedValues.selectedDryGasSealTags ? (
          <ActiveModelsTable data={activeModels} />
        ) : (
          ""
        )}

        {selectedValues.selectedAssembly &&
          selectedValues.selectedDryGasSealTags?.length > 0 &&
          (chartLoading ? (
            <p className="text- text-gray-600">Loading chart. Please wait...</p>
          ) : chartData ? (
            <TimeSeriesChart chartData={chartData} />
          ) : null)}
      </div>

      {isPopupVisible && (
        <ConfirmationPopup
          message={`No valid data available for this tag. Please select a different tag or adjust the date range.`}
          onConfirm={() => setIsPopupVisible(false)}
          onCancel={() => setIsPopupVisible(false)}
          cancelButtonMsg={""}
          confirmButtonMsg={"OK"}
        />
      )}
    </>
  );
};

export default TrainingForm;
